/**
 * Whether an active checkpoint has a microlearning kit (video or article + quiz and/or inspection).
 * Aligns with EditCheckpointsTable / CheckpointEditCard display rules.
 */

export function hasValue(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

export function isProcessingMicrolearningString(value) {
  if (value == null || typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.toLowerCase().includes("<iframe")) {
    return false;
  }
  const lower = trimmed.toLowerCase();
  return (
    lower.includes("being processed") ||
    lower.includes("will be available in a moment") ||
    lower.includes("pending") ||
    (lower.includes("processing") && !/^https?:\/\//i.test(trimmed))
  );
}

/**
 * True when a checkpoint's raw `microlearning` value is a bare/unsigned reference that Cloudflare's
 * `requireSignedURLs` will reject and must be resolved via `getCheckpointVideoSignedUrl` first —
 * i.e. it isn't already iframe HTML, a processing placeholder, or an already-signed playback URL.
 */
export function isApiVideoPlaybackUrl(value) {
  if (!value || typeof value !== "string") return false;
  return /(?:embed\.)?api\.video\/vod/i.test(value.trim());
}

export function needsSignedVideoUrlResolution(value, isAlreadySignedUrl) {
  if (!value || typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.toLowerCase().includes("<iframe")) return false;
  if (isApiVideoPlaybackUrl(trimmed)) return false;
  if (isProcessingMicrolearningString(trimmed)) return false;
  if (typeof isAlreadySignedUrl === "function" && isAlreadySignedUrl(trimmed)) return false;
  return true;
}

export function hasMeaningfulProposedArticle(article) {
  if (article == null) return false;
  let root = article;
  if (typeof root === "string") {
    const trimmed = root.trim();
    if (!trimmed) return false;
    try {
      root = JSON.parse(trimmed);
    } catch {
      return true;
    }
  }
  if (!root || typeof root !== "object") return false;
  if (String(root.title ?? "").trim() !== "") return true;
  const body = root.body;
  if (body == null) return false;
  if (typeof body === "string") return body.trim() !== "";
  return typeof body === "object";
}

export function parseMicrolearningInstructions(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

export function microlearningInstructionsHasLearningContent(raw) {
  const ml = parseMicrolearningInstructions(raw);
  if (!ml || typeof ml !== "object") return false;

  const statusRaw = ml.validation?.status ?? ml.inspection_instructions?.validation?.status ?? null;
  const statusUpper = typeof statusRaw === "string" ? statusRaw.toUpperCase() : "";
  if (statusUpper === "PASS") return true;

  const chapters = ml.table_of_contents?.chapters ?? ml.chapters;
  if (Array.isArray(chapters) && chapters.length > 0) return true;

  if (ml.quiz != null && typeof ml.quiz === "object") return true;

  const steps = ml.inspection_instructions?.steps ?? ml.steps;
  if (Array.isArray(steps) && steps.length > 0) return true;

  if (typeof ml.video_url === "string" && ml.video_url.trim()) return true;

  return false;
}

/** Video asset on a proposed draft — mirrors CheckpointEditFormV2 / active kit display rules. */
export function hasProposedVideoAsset(checkpoint) {
  if (!checkpoint) return false;

  if (
    hasValue(checkpoint.proposed_cloudflarevideo_id) ||
    hasValue(checkpoint.proposed_apivideo_id) ||
    hasValue(checkpoint.proposed_video_id) ||
    hasValue(checkpoint.proposed_video_microlearning) ||
    hasValue(checkpoint.proposed_video_source)
  ) {
    return true;
  }

  if (
    hasValue(checkpoint.microlearning_cloudflare_video_id) ||
    hasValue(checkpoint.microlearning_video_id) ||
    hasValue(checkpoint.microlearning_id)
  ) {
    return true;
  }

  if (
    hasValue(checkpoint.microlearning) &&
    !isProcessingMicrolearningString(String(checkpoint.microlearning))
  ) {
    return true;
  }

  if (microlearningInstructionsHasLearningContent(checkpoint.microlearning_instructions)) {
    return true;
  }

  // propose-from-copy seeds active_* refs; proposed video ids may stay empty until publish.
  if (hasValue(checkpoint.proposed_microlearning_type)) {
    if (
      hasValue(checkpoint.activecloudflarevideo_id) ||
      hasValue(checkpoint.active_apivideo_id) ||
      hasValue(checkpoint.active_video_id) ||
      hasValue(checkpoint.active_video_microlearning) ||
      (hasValue(checkpoint.active_microlearning) &&
        !isProcessingMicrolearningString(String(checkpoint.active_microlearning)))
    ) {
      return true;
    }
  }

  return false;
}

export function hasProposedMicrolearningAsset(checkpoint) {
  if (!checkpoint) return false;
  const mlType = String(checkpoint.proposed_microlearning_type ?? "").toLowerCase();
  if (mlType === "article") {
    return (
      hasMeaningfulProposedArticle(checkpoint.proposed_article) ||
      hasValue(checkpoint.proposed_knowledge_article_html)
    );
  }
  if (mlType === "video") {
    return hasProposedVideoAsset(checkpoint);
  }
  return (
    hasMeaningfulProposedArticle(checkpoint.proposed_article) ||
    hasValue(checkpoint.proposed_cloudflarevideo_id) ||
    hasValue(checkpoint.proposed_apivideo_id) ||
    hasValue(checkpoint.proposed_video_id)
  );
}

/** Active checkpoint has video, article, or instructions-backed microlearning content. */
export function hasActiveMicrolearningAsset(checkpoint) {
  if (!checkpoint) return false;

  if (checkpoint.has_microlearning === true) return true;

  const ml = checkpoint.microlearning;
  if (hasValue(ml) && !isProcessingMicrolearningString(String(ml))) return true;

  if (
    hasValue(checkpoint.active_video_id) ||
    hasValue(checkpoint.active_apivideo_id) ||
    hasValue(checkpoint.activecloudflarevideo_id) ||
    hasValue(checkpoint.microlearning_cloudflare_video_id)
  ) {
    return true;
  }

  const activeInspectionType = String(checkpoint.active_inspection_type ?? "").toLowerCase();
  if (activeInspectionType === "article" || activeInspectionType === "video") {
    return true;
  }

  if (microlearningInstructionsHasLearningContent(checkpoint.microlearning_instructions)) {
    return true;
  }

  if (checkpoint.set_to_microlearning_instructions && hasValue(checkpoint.microlearning)) {
    return true;
  }

  return false;
}

export function hasActiveQuizContent(checkpoint) {
  if (!checkpoint) return false;
  if (hasValue(checkpoint.active_quiz)) return true;

  const ml = parseMicrolearningInstructions(checkpoint.microlearning_instructions);
  if (!ml?.quiz || typeof ml.quiz !== "object") return false;
  if (Array.isArray(ml.quiz.questions) && ml.quiz.questions.length > 0) return true;
  return String(ml.quiz.quiz_title ?? "").trim() !== "";
}

export function hasActiveInspectionContent(checkpoint) {
  if (!checkpoint) return false;
  if (hasValue(checkpoint.active_inspection_type)) return true;
  if (hasValue(checkpoint.active_inspection_instructions)) return true;
  if (hasValue(checkpoint.active_inspection_protocol)) {
    const protocol = parseMaybeJson(checkpoint.active_inspection_protocol);
    if (protocol && typeof protocol === "object") {
      const hasSteps = [
        protocol.steps,
        protocol.inspection_instructions?.steps,
        protocol.instructions?.steps,
      ].some((list) => Array.isArray(list) && list.length > 0);
      if (hasSteps) return true;
    } else if (hasValue(protocol)) {
      return true;
    }
  }

  const ml = parseMicrolearningInstructions(checkpoint.microlearning_instructions);
  const steps = ml?.inspection_instructions?.steps ?? ml?.steps;
  return Array.isArray(steps) && steps.length > 0;
}

/**
 * Active kit: microlearning asset (video or article) plus quiz and/or inspection.
 */
export function hasValidActiveMicrolearningKit(checkpoint) {
  if (!checkpoint) return false;
  const hasAsset = hasActiveMicrolearningAsset(checkpoint);
  const hasQuiz = hasActiveQuizContent(checkpoint);
  const hasInspection = hasActiveInspectionContent(checkpoint);
  return hasAsset && (hasQuiz || hasInspection);
}

const PROPOSED_QUIZ_MODE_VALUES = ["BOTH_REQUIRED", "SKIP_INSPECTION", "SKIP_QUIZ"];

function parseMaybeJson(value) {
  if (value == null) return value;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }
  return value;
}

/**
 * Prefer proposed `quiz_mode`, then legacy `proposed_quiz_is_autopass`, then
 * `active_quiz_mode` (e.g. after propose-change-from-copy clears quiz_mode).
 */
export function resolveProposedQuizMode(checkpoint) {
  const mode = checkpoint?.quiz_mode;
  if (PROPOSED_QUIZ_MODE_VALUES.includes(mode)) return mode;
  if (checkpoint?.proposed_quiz_is_autopass) return "SKIP_INSPECTION";
  const activeMode = checkpoint?.active_quiz_mode;
  if (PROPOSED_QUIZ_MODE_VALUES.includes(activeMode)) return activeMode;
  return "BOTH_REQUIRED";
}

/** True when proposed quiz has questions, or a non-object id/value is set. */
export function hasProposedQuizQuestions(checkpoint) {
  const raw = checkpoint?.proposed_quiz;
  if (!hasValue(raw)) return false;
  const quiz = parseMaybeJson(raw);
  if (quiz == null) return false;
  if (typeof quiz !== "object") return true;
  if (Array.isArray(quiz)) return quiz.length > 0;
  return [quiz.questions, quiz.items, quiz.quiz?.questions, quiz.quiz?.items].some(
    (list) => Array.isArray(list) && list.length > 0
  );
}

/** True when proposed protocol has steps, or a non-object id/value is set. */
export function hasProposedInspectionSteps(checkpoint) {
  const raw = checkpoint?.proposed_inspection_protocol;
  if (!hasValue(raw)) return false;
  const protocol = parseMaybeJson(raw);
  if (protocol == null) return false;
  if (typeof protocol !== "object") return true;
  return [
    protocol.instructions?.steps,
    protocol.instructions?.instructions?.steps,
    protocol.inspection_instructions?.instructions?.steps,
    protocol.inspection_instructions?.steps,
    protocol.steps,
  ].some((list) => Array.isArray(list) && list.length > 0);
}

/** Full proposed draft on checkpoint (e.g. after activate-from-draft). */
export function hasCompleteProposedMicrolearningContent(checkpoint) {
  if (!checkpoint) return false;
  return (
    hasValue(checkpoint.proposed_quiz) &&
    hasValue(checkpoint.proposed_inspection_protocol) &&
    hasValue(checkpoint.proposed_microlearning_type) &&
    hasProposedMicrolearningAsset(checkpoint)
  );
}

/**
 * Proposed article/video kit ready to publish, gated by quiz_mode:
 * Both → asset + questions + steps; Skip inspection → asset + questions; Skip quiz → asset + steps.
 */
export function hasSubstantialProposedMicrolearningKit(checkpoint) {
  if (!checkpoint) return false;
  const mlType = String(checkpoint.proposed_microlearning_type ?? "").toLowerCase();
  if (mlType !== "article" && mlType !== "video") return false;
  if (!hasProposedMicrolearningAsset(checkpoint)) return false;
  const mode = resolveProposedQuizMode(checkpoint);
  if (mode !== "SKIP_QUIZ" && !hasProposedQuizQuestions(checkpoint)) return false;
  if (mode !== "SKIP_INSPECTION" && !hasProposedInspectionSteps(checkpoint)) return false;
  return true;
}

export function shouldShowInvalidMicrolearningNotice(checkpoint, options = {}) {
  const { newInspectionUi = false, status = "" } = options;
  if (!newInspectionUi) return false;
  if (String(status).toLowerCase() !== "active") return false;

  if (hasValidActiveMicrolearningKit(checkpoint)) return false;
  if (hasCompleteProposedMicrolearningContent(checkpoint)) return false;
  if (hasSubstantialProposedMicrolearningKit(checkpoint)) return false;

  return true;
}
